import React, { useState, useRef, useEffect } from 'react';
import { Text, Input, Button, Loading, Select } from "@geist-ui/core";
import { GoogleGenAI } from "@google/genai";
import './Travel.css';
import chesterImage from './chester.webp'; // Import the chester image
import { SYSTEM_PROMPTS, LOCATION_DESCRIPTIONS, API_CONFIG, INTERACTION_THRESHOLDS, STRICT_IMAGE_INSTRUCTIONS } from './constants'; // Import constants

// Initialize the Gemini API
const API_KEY = process.env.REACT_APP_GEMINI_API;
const ai = new GoogleGenAI({ apiKey: API_KEY });

// Location descriptions
const locationDescriptions = LOCATION_DESCRIPTIONS;

const TravelView = () => {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hello! I\'m Chester. Your DM on this trip. Select a location and click "Go" to begin your adventure!' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('langston');
  
  // Add a new state variable to track interaction count
  const [interactionCount, setInteractionCount] = useState(0);
  
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Function to generate an image
  const generateImage = async (responseText) => { // Changed 'prompt' to 'responseText' here
    setImageLoading(true);
    try {
      // Directly concatenate the strings to form the imagePrompt
      const imagePrompt = `Create an environment image of nature and landscapes. Show paths but no direction signs. Detailed, professionally and digitally drawn, fantasy realism art showing: ${responseText}. Instructions: ${STRICT_IMAGE_INSTRUCTIONS}`;
      
      // Use generateImages instead of generateContent
      const response = await ai.models.generateImages({
        model: API_CONFIG.IMAGE_MODEL,
        prompt: imagePrompt, // This is your selected line's context
        config: {
          numberOfImages: 1, // We only need one image
        },
      });
      
      // Check if we have generated images
      if (response.generatedImages && response.generatedImages.length > 0) {
        // Get the image bytes from the first generated image
        const imageBytes = response.generatedImages[0].image.imageBytes;
        // Set the image state with the base64 data
        setGeneratedImage(`data:image/png;base64,${imageBytes}`);
      } else {
        throw new Error("No image was generated in the response");
      }
    } catch (error) {
      console.error('Error generating image:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `I tried to generate an image, but encountered an error: ${error.message}. I'll try again later!` 
      }]);
    } finally {
      setImageLoading(false);
    }
  };

  // Handle location change
  const handleLocationChange = (val) => {
    setSelectedLocation(val);
  };

  // Handle Go button click
  const handleGoButtonClick = async () => {
    // Start loading state
    setIsLoading(true);
    
    // Get location details
    const location = locationDescriptions[selectedLocation];
    
    try {
      // Create a prompt with the journey context - modified to be about traveling TO the location
      const locationPrompt = `You are Chester, the Dungeon Master guiding a player who has decided to travel TO ${location.name}. They haven't arrived yet.

      About the destination - ${location.description}

      Write a brief but vivid introduction that sets up the beginning of their journey toward this location. Describe the possible routes there. They can't see the destination yet. Use second person present tense. Be descriptive about the current nature, flora, fauna, and other environmental surroundings. End with a question that prompts the player to decide how they want to begin this journey.

      Your tone should be immersive and mysterious. Write two paragraphs. Use line breaks. Do not assume the role of the player.`;

      // Call Gemini API
      const response = await ai.models.generateContent({
        model: API_CONFIG.TEXT_MODEL,
        contents: locationPrompt,
        config: {
          systemInstruction: SYSTEM_PROMPTS.CHESTER_DM,
        },
      });

      // Get the response text
      const responseText = response.text;
      
      // Clear previous messages and set the journey introduction
      setMessages([
        { 
          role: 'assistant', 
          content: responseText 
        }
      ]);
      
      // Generate an image using the responseText
      await generateImage(responseText); // This call remains the same, passing the responseText variable
      // Reset the interaction counter when starting a new journey
      setInteractionCount(0);
      
    } catch (error) {
      console.error('Error starting journey adventure:', error);
      setMessages([{ 
        role: 'assistant', 
        content: `Sorry, I encountered an error setting up your journey to ${location.name}: ${error.message || 'Unknown error'}. Please try again.` 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!input.trim()) return;
    
    // Add user message to chat
    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    try {
      // Format all previous messages for context
      let currentConversation = messages.map(msg => {
        return {
          role: msg.role === 'user' ? 'user' : 'model', // Gemini uses 'user' and 'model'
          parts: [{ text: msg.content }]
        };
      });
      // Add the latest user question
      currentConversation.push({ role: 'user', parts: [{ text: userMessage.content }] });


      // Call Gemini API with the simplified format
      const response = await ai.models.generateContent({
        model: API_CONFIG.TEXT_MODEL, 
        contents: currentConversation, // Pass the conversation history
        config: {
          systemInstruction: SYSTEM_PROMPTS.CHESTER_DM,
        },
      });

      // Get the AI's response text
      const responseText = response.text;
      
      // Add assistant response to chat
      const assistantMessage = { 
        role: 'assistant', 
        content: responseText
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      
      // Increment the interaction counter
      const newCount = interactionCount + 1;
      setInteractionCount(newCount);
      
      // Generate an image only every fifth interaction
      if (newCount % INTERACTION_THRESHOLDS.IMAGE_GENERATION === 0) {
        // Use the full response text as the image prompt without length restrictions
        
        // Generate the image based on the AI's response
        await generateImage(responseText); // This call remains the same, passing the responseText variable
      }
      
    } catch (error) {
      console.error('Error communicating with Gemini API:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Sorry, I encountered an error: ${error.message || 'Unknown error'}. Please try again.` 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Add this function above the return statement in the TravelView component
const formatMessageContent = (content) => {
  if (!content) return '';
  
  // Split the content by newlines and map each line to a JSX element
  const lines = content.split('\n');
  
  return lines.map((line, index) => (
    <React.Fragment key={index}>
      {line}
      {index < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

  return (
    <div className="basic-layout">
      {/* Left column - Chat interface */}
      <div className="text-column chat-column">
        <div className="chat-header">
          <div className="chat-header-content">
            <div className="travel-header-row">
              <Text h3 style={{ margin: 0, fontFamily: "Crimson, serif" }}>Travel to</Text>
              <Select 
                size="small" 
                initialValue="langston" 
                value={selectedLocation}
                onChange={handleLocationChange}
                style={{ marginLeft: '10px', minWidth: '120px' }}
              >
                <Select.Option value="langston">Langston</Select.Option>
                <Select.Option value="gritz-manor">Gritz Manor</Select.Option>
                <Select.Option value="open-world">Open World</Select.Option>
              </Select>
              <Button 
                auto
                className="go-button"
                style={{ marginLeft: '10px' }}
                onClick={handleGoButtonClick}
                disabled={isLoading}
              >
                Go
              </Button>
            </div>
          </div>
        </div>
        
        <div className="chat-messages" ref={chatContainerRef}>
          {messages.map((message, index) => (
            <div 
              key={index} 
              className={`message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}
            >
              <div className="message-content">
                {formatMessageContent(message.content)}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="message assistant-message">
              <div className="message-content loading-message">
                <div></div>
                <div></div>
                <div></div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        
        <form className="chat-input-container" onSubmit={handleSendMessage}>
          <Input 
            width="100%" 
            placeholder="Explain your next action in detail to continue..." 
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={isLoading}
          />
          <Button 
            auto
            htmlType="submit" 
            disabled={isLoading || !input.trim()}
            style={{ backgroundColor: "#df2d11", color: "white" }}
          >
            Send
          </Button>
        </form>
      </div>
      
      {/* Right column - Generated image or default Chester image */}
      <div className="image-column">
        {imageLoading ? (
          <div className="image-loading-container">
            <Loading size="large" />
            <Text p style={{ marginTop: "15px" }}>Visualizing the response...</Text>
          </div>
        ) : generatedImage ? (
          <div className="generated-image-container">
            <img 
              src={generatedImage} 
              alt="Generated fantasy scene" 
              className="feature-image generated-image"
            />
          </div>
        ) : (
          <img 
            src={chesterImage} 
            alt="Chester" 
            className="feature-image"
          />
        )}
      </div>
      
      {/* Disclaimer footer */}
      <div className="disclaimer-footer">
        This content is created by Chester, our AI DM, and is currently in beta. AI can make mistakes.
      </div>
    </div>
  );
};

export default TravelView;